import mergeWith from 'lodash/mergeWith';
import { deepClone } from 'core/utils';
import type { DocumentId } from '../../../types';
import { flatByID, createNode } from '../utils';
import type { Node } from '../utils';

export type ItemData<Key extends string> = {
  [x in Key]: unknown;
};

export class DataListManager<TKey extends string, T extends ItemData<TKey>> {
  // static cache = {};

  #list: Map<DocumentId | string, Node<T>>;

  #data: T[];

  #key: TKey;

  // nestedKey = '';

  constructor(data: T[], key: TKey) {
    // if (cacheKey && cacheKey in DataListManager.cache) {
    //   // eslint-disable-next-line no-constructor-return
    //   return DataListManager.cache[cacheKey];
    // }
    // if (nestedKey) this.nestedKey = nestedKey;

    const dataClone: T[] = deepClone(data);

    this.#data = dataClone;
    this.#key = key;
    this.#list = new Map();

    flatByID<TKey, T>(
      dataClone,
      (node: Node<T>) => {
        this.#list.set(node.id, node);
      },
      key,
    );

    // if (cacheKey) DataListManager.cache[cacheKey] = this;
  }

  get data() {
    return this.#data;
  }

  _getFutureIndex(nextId: string, prevId: string) {
    const next = this.getNode(nextId);
    const prev = this.getNode(prevId);

    if (next) {
      return next.indexWithinParent;
    }

    if (prev) {
      return prev.indexWithinParent + 1;
    }

    return 0;
  }

  _getParentArr(parentId: string) {
    if (!parentId || parentId === '0') return this.#data;

    const parent = this.getNode(parentId);

    if (!parent) return null;
    //
    // parent.data[this.nestedKey] = parent.data[this.nestedKey] || [];
    // return parent.data[this.nestedKey];
  }

  _setChildToParent(parentId: string, index: number, itemData: T) {
    const arr = this._getParentArr(parentId);
    if (!arr) return;

    arr.splice(index, 0, itemData);
    this._updateIndexWithinParent(arr);
  }

  _updateIndexWithinParent(arr: T[]) {
    let i = 0;
    while (i < arr.length) {
      const node = this.#list.get(arr[i][this.#key] as string);
      if (node) {
        node.indexWithinParent = i;
        i++;
      }
    }
  }

  getNode(id: DocumentId | string) {
    return this.#list.get(id);
  }

  hasNode(id: DocumentId | string) {
    return this.#list.has(id);
  }

  removeNode(id: DocumentId | string, isTotally = true) {
    if (!this.hasNode(id)) {
      return;
    }

    const node = this.getNode(id);
    if (!node) return;

    const index: number = node.indexWithinParent;

    // remove item from list
    if (isTotally) this.#list.delete(id);

    const arr = this._getParentArr(node.parent);
    if (!arr) return;

    arr.splice(index, 1);
    this._updateIndexWithinParent(arr);
  }

  addNode(id: DocumentId | string, itemData: T, parentId = '', nextId = '', prevId = '') {
    if (this.hasNode(id)) {
      this.moveNode(id, parentId, nextId, prevId);
      return;
    }

    const index = this._getFutureIndex(nextId, prevId);
    const node = createNode({
      id,
      data: itemData,
      parent: parentId,
      next: nextId,
      prev: prevId,
      index,
    });

    // update list
    this.#list.set(id, node);

    this._setChildToParent(parentId, index, itemData);
  }

  moveNode(id: DocumentId | string, parentId = '', nextId = '', prevId = '') {
    const node = this.getNode(id);
    if (!node) return;

    // remove item from old parent
    this.removeNode(id, false);

    const getFutureIndex = () => {
      if (nextId && node.next === nextId) {
        return this._getFutureIndex('', prevId);
      }

      return this._getFutureIndex(nextId, prevId);
    };

    const index = getFutureIndex();

    node.parent = parentId;
    node.next = nextId;
    node.prev = prevId;

    this._setChildToParent(parentId, index, node.data);
  }

  updateNode(id: DocumentId | string, data: Partial<ItemData<TKey>>) {
    if (!this.hasNode(id)) {
      return;
    }
    const item = this.getNode(id);
    if (!item) return;
    mergeWith(item.data, data, (model: unknown, patch: unknown) => {
      if (Array.isArray(model)) return patch;
    });
  }
}
